import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, UserCheck, UserX, CalendarPlus } from 'lucide-react';
import { useI18n } from '../../context/useI18n';
import { isD28dDomainUser, isFoodCommercialPlan, foodOnlyModuleAccess, wantsD28dProductAccess, isPlatformD28dAddonPlan } from '../../utils/adminUserScopes';
import { gymSelectOptionLabel } from '../../utils/organizationView';
import InviteCodeCell from './InviteCodeCell';
import GymMinimalBrandingFields from './GymMinimalBrandingFields';
import PhoneField from '../ui/PhoneField';
import { isPostpartumMom } from '../../utils/postpartumFlag';
import { formatCycleDate, filterPurchasableCycles } from '../../utils/cycleUtils';
import {
  consecutiveCyclePack,
  cycleIdsFromPack,
  planCyclesIncluded,
} from '../../utils/planCyclePack';

/** YYYY-MM-DD civil (Bogotá) para inputs date — evita desfase UTC. */
function dateInputValue(value) {
  if (!value) return '';
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)) return value.slice(0, 10);
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  try {
    return new Intl.DateTimeFormat('en-CA', {
      timeZone: 'America/Bogota',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(d);
  } catch {
    return d.toISOString().slice(0, 10);
  }
}

function todayLocalISO() {
  return dateInputValue(new Date());
}

function addDaysLocalISO(iso, days) {
  const base = iso && /^\d{4}-\d{2}-\d{2}/.test(iso) ? iso.slice(0, 10) : todayLocalISO();
  const d = new Date(`${base}T12:00:00`);
  d.setDate(d.getDate() + (Number(days) || 0));
  return dateInputValue(d);
}

function daysBetweenLocalISO(start, end) {
  if (!start || !end) return null;
  const a = new Date(`${String(start).slice(0, 10)}T12:00:00`);
  const b = new Date(`${String(end).slice(0, 10)}T12:00:00`);
  if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime())) return null;
  return Math.max(1, Math.round((b.getTime() - a.getTime()) / 86400000));
}

function userGymIdFromUser(user) {
  const raw = user?.gym_id ?? user?.gymId;
  if (raw == null || raw === '') return null;
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : null;
}

function isTrainingCoachClient(user, formData = {}) {
  const roles = Array.isArray(formData.roles) && formData.roles.length
    ? formData.roles
    : (user?.roles?.length ? user.roles : [user?.rol].filter(Boolean));
  if (!roles.includes('usuario_final')) return false;
  const trainerId = formData.trainer_id || user?.trainer_id;
  return Boolean(trainerId);
}

const MODULE_OPTIONS = [
  { key: 'gym', label: 'Gimnasio (producto)' },
  { key: 'd28d', label: 'Programas D28D' },
  { key: 'training', label: 'Entrenadores' },
  { key: 'food_plan', label: 'Plan de alimentación' },
  { key: 'live_classes', label: 'Clases en vivo' },
];

const SUBSCRIPTION_ESTADO_BADGE = {
  activo: 'badge-green',
  pendiente_sede: 'badge-warning',
  pendiente_pago_online: 'badge-warning',
  pendiente_habilitacion: 'badge-warning',
  pendiente_activacion: 'badge-warning',
  pendiente: 'badge-warning',
  vencido: 'badge-danger',
  cancelado: 'badge-danger',
  rechazado: 'badge-danger',
};

function hasActiveOperatorLicenses(licenses = []) {
  const core = new Set(['d28d', 'gym', 'live_classes']);
  const now = Date.now();
  return licenses.some((lic) => {
    if (!lic?.active || !core.has(lic.module_code)) return false;
    if (lic.valid_from) {
      const from = new Date(lic.valid_from).getTime();
      if (Number.isFinite(from) && from > now) return false;
    }
    if (!lic.valid_until) return true;
    return new Date(lic.valid_until).getTime() >= now;
  });
}

function formatSubDate(value) {
  if (!value) return null;
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleDateString();
  } catch {
    return null;
  }
}

/** Días restantes alineados a la API: si inicio futuro → ventana; si no → hoy→fin. */
function daysFromInicioToFin(sub) {
  if (sub?.days_left != null && Number.isFinite(Number(sub.days_left))) {
    return Number(sub.days_left);
  }
  const inicioRaw = sub?.fecha_inicio || sub?.cycle_enable_date || sub?.cycle_start_date || null;
  const finRaw = sub?.fecha_vencimiento || null;
  if (!finRaw) return null;
  const end = new Date(finRaw);
  if (Number.isNaN(end.getTime())) return null;
  const start = inicioRaw ? new Date(inicioRaw) : null;
  const now = Date.now();
  if (start && !Number.isNaN(start.getTime()) && start.getTime() > now) {
    return Math.ceil((end.getTime() - start.getTime()) / 86400000);
  }
  return Math.ceil((end.getTime() - now) / 86400000);
}

function formatSubscriptionEstado(sub, t) {
  if (sub?.operator_license && sub?.active && !sub?.has_account) return t('users.sub_active', 'Activo');
  if (!sub?.has_account && !sub?.operator_license) return t('users.sub_operator_pending', 'Pendiente vigencia operador');
  const estado = String(sub.estado || '').toLowerCase();
  if (sub.pending || [
    'pendiente_activacion', 'pendiente_habilitacion', 'pendiente_sede',
    'pendiente_pago_online', 'pendiente',
  ].includes(estado)) {
    return t('users.sub_pending', 'Pendiente de activación');
  }
  if (estado === 'activo') return t('users.sub_active', 'Activo');
  if (estado === 'vencido') return t('users.sub_expired', 'Vencido');
  if (estado === 'rechazado') return t('users.sub_rejected', 'Rechazado');
  return sub.estado || '—';
}

function formatDaysLeft(sub, t) {
  const days = daysFromInicioToFin(sub);
  if (sub?.operator_license && sub?.active && days != null) {
    if (days < 0) {
      return t('users.days_overdue', 'Vencido ({n}d)').replace('{n}', String(Math.abs(days)));
    }
    if (days === 0) return t('users.days_today', 'Vence hoy');
    return `${days}d`;
  }
  if (!sub?.has_account) return '—';
  if (sub.pending && days == null) return '—';
  if (days == null) return '—';
  if (days < 0) {
    return t('users.days_overdue', 'Vencido ({n}d)').replace('{n}', String(Math.abs(days)));
  }
  if (days === 0) return t('users.days_today', 'Vence hoy');
  return `${days}d`;
}

function cycleOptionLabel(c) {
  if (!c) return '';
  const start = formatCycleDate(c.startDate || c.start_date);
  const end = formatCycleDate(c.endDate || c.end_date);
  const range = start && end ? ` (${start} – ${end})` : (start ? ` (${start})` : '');
  return `${c.name || `Ciclo ${c.id}`}${range}`;
}

export default function EditUserModal({
  open,
  user,
  formData,
  setFormData,
  saving = false,
  loading = false,
  error = '',
  onClose,
  onSubmit,
  plans = [],
  cycles = [],
  programs = [],
  trainers = [],
  gyms = [],
  isGymScoped = false,
  isGymSimpleForm = false,
  isCoachActor = false,
  coachTrainingPlans = [],
  isSuperAdmin = false,
  isPlatformD28dAdmin = false,
  scopeGymId = null,
  scopeGymName = '',
  gymById = {},
  roleOptionsForForm = [],
  coachModuleOptions = [],
  formatRole = (r) => r,
  showSubscriptionActions = false,
  subscriptionActivating = false,
  onActivate,
  onDeactivate,
  onExtend,
  parentSedePrograms = [],
  sedeCatalogPlans = [],
  onSaveSedeInviteCode,
  onGenerateSedeInviteCode,
  onSwitchToUserEdit,
}) {
  const { t } = useI18n();

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape' && !saving) onClose?.(); };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [open, saving, onClose]);

  // Debe ir antes de cualquier return: si loading abre/cierra el modal, no puede
  // aparecer/desaparecer un useEffect (React #310).
  useEffect(() => {
    if (!open || !user || loading) return;
    const selectedPlan = plans.find((p) => p.nombre === formData.planId);
    if (isFoodCommercialPlan(selectedPlan) || isFoodCommercialPlan({ plan: formData.planId, planId: formData.planId })) {
      return;
    }
    const cyclesIncluded = planCyclesIncluded(selectedPlan);
    if (cyclesIncluded <= 1) return;

    const allowed = Array.isArray(selectedPlan?.cycle_ids)
      ? selectedPlan.cycle_ids.map(Number).filter(Number.isFinite)
      : [];
    let pool = allowed.length
      ? cycles.filter((c) => allowed.includes(Number(c.id)))
      : cycles;
    if (allowed.length && !pool.length) pool = cycles;
    const purchasable = filterPurchasableCycles(pool);
    const currentId = formData.cycle_id || user?.subscription?.cycle_id;
    let cyclesForPlan = purchasable;
    if (currentId && !purchasable.some((c) => String(c.id) === String(currentId))) {
      const orphan = cycles.find((c) => String(c.id) === String(currentId));
      cyclesForPlan = orphan
        ? [orphan, ...purchasable]
        : [{ id: Number(currentId) || currentId, name: `Ciclo ${currentId}`, startDate: null, endDate: null }, ...purchasable];
    }
    if (!cyclesForPlan.length) return;

    let cycleSelectValue = '';
    if (currentId) {
      if (cyclesForPlan.some((c) => String(c.id) === String(currentId))) {
        cycleSelectValue = String(currentId);
      } else {
        const raw = cycles.find((c) => Number(c.id) === Number(currentId));
        const startKey = String(raw?.startDate || raw?.start_date || '').slice(0, 10);
        const mirror = startKey
          ? cyclesForPlan.find((c) => String(c.startDate || c.start_date || '').slice(0, 10) === startKey)
          : null;
        cycleSelectValue = mirror ? String(mirror.id) : String(currentId);
      }
    }
    if (!cycleSelectValue) return;

    const pack = consecutiveCyclePack(cyclesForPlan, cycleSelectValue, cyclesIncluded);
    const nextIds = cycleIdsFromPack(pack);
    const prevIds = Array.isArray(formData.cycle_ids) ? formData.cycle_ids.map(String) : [];
    if (nextIds.length && nextIds.join(',') !== prevIds.join(',')) {
      setFormData((prev) => ({
        ...prev,
        cycle_id: nextIds[0] || prev.cycle_id,
        cycle_ids: nextIds,
      }));
    }
  }, [
    open,
    user,
    loading,
    formData.planId,
    formData.cycle_id,
    formData.cycle_ids,
    plans,
    cycles,
    setFormData,
  ]);

  if (!open || !user) return null;

  if (loading) {
    const loadingNode = (
      <div className="form-modal-overlay" role="dialog" aria-modal="true">
        <div className="form-modal d28d-admin-modal ui-light-panel surface-readable max-w-md p-8 text-center bg-white border border-slate-200 rounded-2xl">
          <p className="text-sm text-stone-600">{t('common.loading', 'Cargando...')}</p>
        </div>
      </div>
    );
    if (typeof document === 'undefined') return loadingNode;
    return createPortal(loadingNode, document.body);
  }

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => {
      const next = {
        ...prev,
        [name]: type === 'checkbox' ? checked : value,
      };
      if (name === 'planId') {
        next.cycle_id = '';
        next.cycle_ids = [];
        const plan = plans.find((p) => p.nombre === value);
        if (isFoodCommercialPlan(plan) || isFoodCommercialPlan({ plan: value, planId: value })) {
          next.gym_id = '';
          const alreadyFood = isFoodCommercialPlan({ plan: prev.planId, planId: prev.planId });
          if (!alreadyFood) next.module_access = foodOnlyModuleAccess();
          if (!next.extend_vigencia_days) next.extend_vigencia_days = '30';
        }
      }
      if (name === 'fecha_inicio' || name === 'fecha_vencimiento') {
        const start = name === 'fecha_inicio' ? value : (prev.fecha_inicio || todayLocalISO());
        let end = name === 'fecha_vencimiento' ? value : prev.fecha_vencimiento;
        if (name === 'fecha_inicio' && !end) {
          const days = Number(prev.extend_vigencia_days) || 30;
          end = addDaysLocalISO(start, days);
          next.fecha_vencimiento = end;
        }
        const span = daysBetweenLocalISO(start, end || next.fecha_vencimiento);
        if (span != null) next.extend_vigencia_days = String(span);
      }
      if (name === 'extend_vigencia_days') {
        const n = Number(value);
        if (Number.isFinite(n) && n >= 1 && n <= 365) {
          const start = prev.fecha_inicio || todayLocalISO();
          next.fecha_inicio = start;
          next.fecha_vencimiento = addDaysLocalISO(start, n);
        }
      }
      return next;
    });
  };

  const isSedeEdit = formData.editKind === 'sede';
  const userRoles = user.roles?.length ? user.roles : [user.rol].filter(Boolean);
  const GYM_MEMBER_ROLES = new Set(['usuario_final', 'usuario_final_sede']);
  const isPrivilegedTarget = userRoles.some((r) => r && !GYM_MEMBER_ROLES.has(r));
  const isConsumer = !isPrivilegedTarget
    && (formData.roles?.includes('usuario_final') || userRoles.some((r) => GYM_MEMBER_ROLES.has(r)));
  const selectedPlan = plans.find((p) => p.nombre === formData.planId);
  const foodPlanSelected = isFoodCommercialPlan(selectedPlan)
    || isFoodCommercialPlan({ plan: formData.planId, planId: formData.planId });
  const wantsD28dAddon = isSuperAdmin && wantsD28dProductAccess(formData.module_access);
  const isTrainingClient = !foodPlanSelected && isTrainingCoachClient(user, formData);
  const showD28dPlacement = !isTrainingClient && (!foodPlanSelected || wantsD28dAddon);
  const d28dAddonPlans = (plans || []).filter(isPlatformD28dAddonPlan);
  const addonPlan = d28dAddonPlans.find((p) => p.nombre === formData.d28d_addon_plan) || d28dAddonPlans[0];
  const isFinalUserEdit = formData.editKind === 'final_user' || isConsumer;
  const showMemberFields = isFinalUserEdit && !isSedeEdit;
  const isGymMemberEdit = isGymSimpleForm && (
    formData.editKind === 'final_user' || userRoles.some((r) => GYM_MEMBER_ROLES.has(r))
  );
  const showPlanFields = showMemberFields && !isTrainingClient && !isPrivilegedTarget && (
    isGymMemberEdit || isSuperAdmin || isPlatformD28dAdmin
  );
  const showTrainingSubscription = showMemberFields && isTrainingClient;

  const parentGymId = isGymScoped && scopeGymId ? Number(scopeGymId) : null;
  const gymPrograms = (() => {
    if (isSedeEdit && parentSedePrograms.length) return parentSedePrograms;
    if (!isGymScoped || !scopeGymId) return programs;
    const gid = Number(scopeGymId);
    const gym = gymById[gid];
    const calendarIds = gym?.calendar_program_ids;
    if (Array.isArray(calendarIds) && calendarIds.length) {
      return programs.filter((p) => calendarIds.some((id) => String(id) === String(p.id)));
    }
    const parentId = gym?.parent_gym_id ? Number(gym.parent_gym_id) : gid;
    return programs.filter((p) => Number(p.gym_id) === parentId || Number(p.gym_id) === gid);
  })();

  const acquiredPlanName = formData.planId || user?.subscription?.plan || user?.planId || '';
  const cyclesIncluded = planCyclesIncluded(selectedPlan);
  const cyclesForPlan = (() => {
    if (foodPlanSelected && !wantsD28dAddon) return [];
    const planForCycles = (foodPlanSelected && wantsD28dAddon) ? addonPlan : selectedPlan;
    const allowed = Array.isArray(planForCycles?.cycle_ids)
      ? planForCycles.cycle_ids.map(Number).filter(Number.isFinite)
      : [];
    let pool = allowed.length
      ? cycles.filter((c) => allowed.includes(Number(c.id)))
      : cycles;
    // cycle_ids del plan en ids de otro ámbito → pool vacío; usar catálogo cargado.
    if (allowed.length && !pool.length) pool = cycles;
    const purchasable = filterPurchasableCycles(pool);
    const currentId = formData.cycle_id || user?.subscription?.cycle_id;
    if (currentId && !purchasable.some((c) => String(c.id) === String(currentId))) {
      const orphan = cycles.find((c) => String(c.id) === String(currentId));
      if (orphan) return [orphan, ...purchasable];
      return [
        {
          id: Number(currentId) || currentId,
          name: `Ciclo ${currentId}`,
          startDate: null,
          endDate: null,
        },
        ...purchasable,
      ];
    }
    return purchasable;
  })();

  const cycleSelectValue = (() => {
    const currentId = formData.cycle_id || '';
    if (!currentId) return '';
    if (cyclesForPlan.some((c) => String(c.id) === String(currentId))) return String(currentId);
    const raw = cycles.find((c) => Number(c.id) === Number(currentId));
    const startKey = String(raw?.startDate || raw?.start_date || '').slice(0, 10);
    const mirror = startKey
      ? cyclesForPlan.find((c) => String(c.startDate || c.start_date || '').slice(0, 10) === startKey)
      : null;
    return mirror ? String(mirror.id) : String(currentId);
  })();

  const selectedCycleIds = Array.isArray(formData.cycle_ids) && formData.cycle_ids.length
    ? formData.cycle_ids.map(String)
    : (cycleSelectValue
      ? cycleIdsFromPack(consecutiveCyclePack(cyclesForPlan, cycleSelectValue, cyclesIncluded))
      : []);

  const pickCyclePackStart = (startId) => {
    const pack = consecutiveCyclePack(cyclesForPlan, startId, cyclesIncluded);
    setFormData((prev) => ({
      ...prev,
      cycle_id: pack[0] ? String(pack[0].id) : String(startId || ''),
      cycle_ids: cycleIdsFromPack(pack),
    }));
  };

  const currentProgramId = formData.d28d_program
    || selectedPlan?.program_id
    || user?.module_access?.d28d_program
    || '';

  const modalTitle = isSedeEdit
    ? t('users.edit_sede', 'Editar sede')
    : t('users.edit_user', 'Editar Usuario');

  const toggleSedeProgram = (programId) => {
    setFormData((prev) => {
      const id = String(programId);
      const has = prev.sede_calendar_program_ids.some((p) => String(p) === id);
      return {
        ...prev,
        sede_calendar_program_ids: has
          ? prev.sede_calendar_program_ids.filter((p) => String(p) !== id)
          : [...prev.sede_calendar_program_ids, programId],
      };
    });
  };

  const renderSubscriptionSection = (noAccountHint) => {
    const operatorLicensesActive = hasActiveOperatorLicenses(user?.licenses);
    const subscriptionActive = Boolean(user.subscription?.active);
    const showAsActive = user.subscription?.has_account
      ? subscriptionActive
      : (subscriptionActive || user.subscription?.operator_license || operatorLicensesActive);
    // Solo operadores sin cuenta comercial (admin sede). Consumidor con cuenta ≠ mensaje operador.
    const showOperatorActive = Boolean(
      !user.subscription?.has_account
      && (user.subscription?.operator_license || operatorLicensesActive),
    );

    return (
    <>
      <label className="block text-sm">
        <span className="font-semibold text-stone-800">{t('plans.subscription', 'Plan de Suscripción')}</span>
        <select name="planId" value={formData.planId || ''} onChange={handleChange} className="input mt-1 w-full" disabled={user.subscription?.operator_license}>
          <option value="">{t('common.none', 'Ninguno')}</option>
          {acquiredPlanName && !plans.some((p) => p.nombre === acquiredPlanName) ? (
            <option value={acquiredPlanName}>{String(acquiredPlanName).toUpperCase()}</option>
          ) : null}
          {plans.map((p) => (
            <option key={p.nombre} value={p.nombre}>{(p.display_nombre || p.nombre).toUpperCase()}</option>
          ))}
        </select>
      </label>
      {user?.subscription?.is_couple ? (
        <p className="text-xs text-violet-800 -mt-1 mb-2">
          {user.subscription.couple_role === 'invitado'
            ? t(
              'users.couple_guest_hint',
              'Plan pareja · invitado (cuenta amarrada al titular). Se administra aparte; vigencia comercial sigue al titular.',
            )
            : t(
              'users.couple_titular_hint',
              'Plan pareja · titular. Al desactivar este usuario también se suspende la pareja vinculada.',
            )}
          {user.subscription.couple_role === 'invitado' && user.subscription.couple_primary_user_id
            ? ` · titular #${user.subscription.couple_primary_user_id}`
            : ''}
          {user.subscription.couple_role === 'titular' && user.subscription.couple_partner_user_id
            ? ` · pareja #${user.subscription.couple_partner_user_id}`
            : ''}
        </p>
      ) : null}

      {(showPlanFields || acquiredPlanName || formData.cycle_id || user?.subscription?.cycle_id) && showD28dPlacement && (
        <div className="block text-sm">
          <span className="font-semibold text-stone-800">
            {cyclesIncluded > 1
              ? t('users.activation_cycles_pack', 'Paquete de ciclos de activación')
              : t('users.activation_cycle', 'Ciclo de activación')}
          </span>
          {cyclesIncluded > 1 ? (
            <>
              <p className="text-xs text-stone-500 mt-1 mb-2">
                {t(
                  'users.activation_cycles_pack_hint',
                  'Este plan incluye {n} ciclos consecutivos. Elige el ciclo de inicio; se marcan los siguientes automáticamente.',
                ).replace('{n}', String(cyclesIncluded))}
              </p>
              <div className="mt-1 max-h-48 overflow-y-auto rounded-xl border border-stone-200 bg-white p-2 space-y-1">
                {cyclesForPlan.map((c) => {
                  const id = String(c.id);
                  const checked = selectedCycleIds.includes(id);
                  return (
                    <label
                      key={c.id}
                      className={`flex items-start gap-2 rounded-lg px-2 py-1.5 text-sm cursor-pointer ${
                        checked ? 'bg-amber-50 border border-amber-200' : 'hover:bg-stone-50'
                      }`}
                    >
                      <input
                        type="checkbox"
                        className="mt-1"
                        checked={checked}
                        disabled={user.subscription?.operator_license}
                        onChange={() => pickCyclePackStart(id)}
                      />
                      <span>{cycleOptionLabel(c)}</span>
                    </label>
                  );
                })}
              </div>
              {selectedCycleIds.length > 0 && (
                <p className="text-xs text-stone-600 mt-2">
                  {t('users.activation_cycles_selected', 'Activo en')}:{' '}
                  <strong>{selectedCycleIds.length}/{cyclesIncluded}</strong>
                  {selectedCycleIds.length < cyclesIncluded
                    ? ` — ${t('users.activation_cycles_need_more', 'faltan ciclos posteriores en el catálogo')}`
                    : null}
                </p>
              )}
              <input type="hidden" name="cycle_id" value={cycleSelectValue} />
            </>
          ) : (
            <select
              name="cycle_id"
              value={cycleSelectValue}
              onChange={handleChange}
              className="input mt-1 w-full"
              disabled={user.subscription?.operator_license}
              required={cyclesForPlan.length > 0}
            >
              <option value="">
                {cyclesForPlan.length
                  ? t('users.cycle_select_ph', 'Selecciona el ciclo')
                  : t('users.cycle_auto', 'Sin ciclo / automático')}
              </option>
              {cyclesForPlan.map((c) => (
                <option key={c.id} value={c.id}>{cycleOptionLabel(c)}</option>
              ))}
            </select>
          )}
          {user?.subscription?.cycle_id ? (
            <p className="text-xs text-stone-500 mt-1">
              {t('users.cycle_saved_hint', 'Ciclo guardado en cuenta')}:{' '}
              {cycleOptionLabel(cycles.find((c) => Number(c.id) === Number(user.subscription.cycle_id)))
                || user.subscription.cycle_id}
            </p>
          ) : null}
        </div>
      )}

      <fieldset className="border border-lime-200 rounded-xl p-4 bg-lime-50/50 space-y-3">
        <legend className="text-sm font-semibold text-stone-800 px-1">{t('users.subscription_section', 'Suscripción')}</legend>
        {showAsActive ? (
          <>
            <div className="flex flex-wrap gap-3 text-sm text-stone-700">
              <span>
                {t('users.sub_status', 'Estado')}:{' '}
                <span className={`badge ${SUBSCRIPTION_ESTADO_BADGE[String(user.subscription.estado || '').toLowerCase()] || 'badge-slate'} text-xs`}>
                  {formatSubscriptionEstado(user.subscription, t)}
                </span>
              </span>
              <span>{t('users.days_remaining', 'Días restantes')}: {formatDaysLeft(user.subscription, t)}</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <label className="block text-sm">
                <span className="font-semibold text-stone-800">{t('users.valid_from', 'Inicio de vigencia')}</span>
                <input
                  name="fecha_inicio"
                  type="date"
                  value={formData.fecha_inicio || ''}
                  onChange={handleChange}
                  className="input mt-1 w-full"
                />
              </label>
              <label className="block text-sm">
                <span className="font-semibold text-stone-800">{t('users.valid_until', 'Vigencia hasta')}</span>
                <input
                  name="fecha_vencimiento"
                  type="date"
                  value={formData.fecha_vencimiento || ''}
                  onChange={handleChange}
                  className="input mt-1 w-full"
                  min={formData.fecha_inicio || undefined}
                />
              </label>
            </div>
            {((user.subscription.cycle_start_date
              && String(user.subscription.cycle_start_date).slice(0, 10) > new Date().toISOString().slice(0, 10))
              || String(user.subscription.estado || '').toLowerCase() === 'pendiente_habilitacion') ? (
                <p className="text-sm text-teal-900 bg-teal-50 border border-teal-200 rounded-lg px-3 py-2">
                  {t(
                    'users.deferred_cycle_editor_note',
                    'Plan/ciclo adelantado: el acceso del consumidor se habilita un día antes del inicio del ciclo ({cycle} · {date}).',
                  )
                    .replace('{cycle}', user.subscription.cycle_name || `Ciclo ${user.subscription.cycle_id || ''}`)
                    .replace(
                      '{date}',
                      formatSubDate(user.subscription.cycle_start_date || user.subscription.cycle_enable_date) || '—',
                    )}
                </p>
              ) : null}
            {showOperatorActive ? (
              <p className="text-sm text-green-800 bg-green-50 border border-green-200 rounded-lg px-3 py-2">
                {t('users.sede_operator_active', 'Administrador de sede con acceso operativo activo (licencias D28D, gym y clases en vivo).')}
              </p>
            ) : null}
            <label className="block text-sm">
              <span className="font-semibold text-stone-800">{t('users.extend_days_on_save', 'Ampliar vigencia al guardar (días)')}</span>
              <input
                name="extend_vigencia_days"
                type="number"
                min="1"
                max="365"
                value={formData.extend_vigencia_days}
                onChange={handleChange}
                className="input mt-1 w-full max-w-xs"
                placeholder="30"
              />
            </label>
            {showSubscriptionActions && !showOperatorActive && (
              <div className="admin-row-actions justify-start">
                {user.subscription?.pending || !user.subscription?.active ? (
                  <button type="button" className="admin-action admin-action--success" onClick={() => onActivate?.(user)} disabled={subscriptionActivating}>
                    <UserCheck className="w-4 h-4" />
                    {subscriptionActivating ? t('users.activating', 'Activando…') : t('users.activate_user', 'Activar')}
                  </button>
                ) : (
                  <button type="button" className="admin-action admin-action--neutral" onClick={() => onDeactivate?.(user)} disabled={subscriptionActivating}>
                    <UserX className="w-4 h-4" />
                    {t('users.deactivate_user', 'Desactivar')}
                  </button>
                )}
              </div>
            )}
          </>
        ) : (
          <>
            <p className="text-sm text-stone-700">{noAccountHint}</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <label className="block text-sm">
                <span className="font-semibold text-stone-800">{t('users.valid_from', 'Inicio de vigencia')}</span>
                <input
                  name="fecha_inicio"
                  type="date"
                  value={formData.fecha_inicio || ''}
                  onChange={handleChange}
                  className="input mt-1 w-full"
                  disabled={subscriptionActivating}
                />
              </label>
              <label className="block text-sm">
                <span className="font-semibold text-stone-800">{t('users.valid_until', 'Vigencia hasta')}</span>
                <input
                  name="fecha_vencimiento"
                  type="date"
                  value={formData.fecha_vencimiento || ''}
                  onChange={handleChange}
                  className="input mt-1 w-full"
                  min={formData.fecha_inicio || undefined}
                  disabled={subscriptionActivating}
                />
              </label>
            </div>
            <label className="block text-sm">
              <span className="font-semibold text-stone-800">{t('users.extend_days_on_save', 'Días de vigencia al activar')}</span>
              <input
                name="extend_vigencia_days"
                type="number"
                min="1"
                max="365"
                value={formData.extend_vigencia_days}
                onChange={handleChange}
                className="input mt-1 w-full max-w-xs"
                placeholder="30"
                disabled={subscriptionActivating}
              />
            </label>
            {showSubscriptionActions && (user.subscription?.pending || user.subscription?.pending_registration || !user.subscription?.active) && (
              <button
                type="button"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-green-300 text-green-800 bg-green-50 hover:bg-green-100 text-sm disabled:opacity-60"
                onClick={() => onActivate?.(user)}
                disabled={subscriptionActivating}
              >
                <UserCheck className="w-4 h-4" />
                {subscriptionActivating ? t('users.activating', 'Activando…') : t('users.activate_user', 'Activar')}
              </button>
            )}
          </>
        )}
      </fieldset>
    </>
    );
  };

  const modal = (
    <div className="form-modal-overlay" role="dialog" aria-modal="true" onClick={() => !saving && onClose?.()}>
      <div
        className="form-modal d28d-admin-modal ui-light-panel surface-readable bg-white border border-slate-200 w-full max-w-2xl !overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="form-modal-header">
          <h3 className="font-bold text-stone-900">{modalTitle}</h3>
          <button type="button" className="form-close-btn" onClick={onClose} disabled={saving} aria-label={t('common.close', 'Cerrar')}>
            <X size={18} />
          </button>
        </div>

        <form className="form-modal-content space-y-4" onSubmit={onSubmit}>
          {error ? <div className="alert-error text-sm">{error}</div> : null}

          {isSedeEdit ? (
            <>
              <p className="text-sm text-stone-600">
                {t('users.edit_sede_hint', 'Edita la sede, su código de invitación, programas, cobro y datos del administrador.')}
              </p>
              <div className="grid sm:grid-cols-2 gap-4">
                <label className="block text-sm">
                  <span className="font-semibold text-stone-800">{t('orgs.field_sede_name', 'Nombre de la sede')}</span>
                  <input name="sede_nombre" value={formData.sede_nombre} onChange={handleChange} required className="input mt-1 w-full" />
                </label>
                <label className="block text-sm">
                  <span className="font-semibold text-stone-800">{t('common.city', 'Ciudad')}</span>
                  <input name="sede_ciudad" value={formData.sede_ciudad} onChange={handleChange} className="input mt-1 w-full" />
                </label>
                <label className="block text-sm">
                  <span className="font-semibold text-stone-800">{t('tenants.field_contact_email', 'Email de contacto')}</span>
                  <input name="sede_contact_email" type="email" value={formData.sede_contact_email} onChange={handleChange} className="input mt-1 w-full" />
                </label>
                <label className="block text-sm">
                  <span className="font-semibold text-stone-800">{t('orgs.sede_member_fee', 'Valor de cobro (COP)')}</span>
                  <input name="sede_member_fee_cop" type="number" min="0" step="1000" value={formData.sede_member_fee_cop} onChange={handleChange} className="input mt-1 w-full" />
                </label>
              </div>

              <div className="block text-sm">
                <span className="font-semibold text-stone-800">{t('users.invite_code', 'Código invitación')}</span>
                <div className="mt-1">
                  <InviteCodeCell
                    value={formData.sede_invite_code}
                    onSave={async (code) => {
                      if (formData.sede_gym_id && onSaveSedeInviteCode) {
                        const saved = await onSaveSedeInviteCode(Number(formData.sede_gym_id), code);
                        setFormData((prev) => ({ ...prev, sede_invite_code: saved || code }));
                        return saved;
                      }
                      setFormData((prev) => ({ ...prev, sede_invite_code: code }));
                      return code;
                    }}
                    onGenerate={formData.sede_gym_id && onGenerateSedeInviteCode
                      ? () => onGenerateSedeInviteCode(Number(formData.sede_gym_id))
                      : null}
                  />
                </div>
              </div>

              <label className="flex items-center gap-2 text-sm text-stone-700 cursor-pointer">
                <input
                  type="checkbox"
                  name="sede_skip_member_payment"
                  checked={formData.sede_skip_member_payment === true}
                  onChange={handleChange}
                />
                {t('orgs.sede_skip_payment', 'Usuarios registrados por código no pagan en línea (habilitación manual)')}
              </label>

              <label className="flex items-center gap-2 text-sm text-stone-700 cursor-pointer">
                <input
                  type="checkbox"
                  name="sede_allow_food_addon"
                  checked={formData.sede_allow_food_addon !== false}
                  onChange={handleChange}
                />
                {t('orgs.sede_allow_food_addon', 'Mostrar «Adquirir FOOD» a usuarios de la sede')}
              </label>

              <div>
                <label className="label">{t('orgs.sede_plan', 'Plan de la sede')} *</label>
                <select
                  name="sede_plan_id"
                  value={formData.sede_plan_id || ''}
                  onChange={handleChange}
                  className="input mt-1 w-full"
                  required
                >
                  <option value="">{t('orgs.sede_plan_placeholder', 'Selecciona un plan de sedes')}</option>
                  {sedeCatalogPlans.map((p) => (
                    <option key={p.nombre} value={p.nombre}>
                      {(p.display_nombre || p.nombre)}
                      {Number(p.precio_mensual) === 0 && Number(p.precio_mensual_usd) === 0
                        ? ' · $0'
                        : ` · $${Number(p.precio_mensual || 0).toLocaleString('es-CO')} COP`}
                    </option>
                  ))}
                  {formData.sede_plan_id
                    && !sedeCatalogPlans.some((p) => p.nombre === formData.sede_plan_id) ? (
                      <option value={formData.sede_plan_id}>{formData.sede_plan_id}</option>
                    ) : null}
                </select>
                <p className="text-xs text-stone-500 mt-1">
                  {t(
                    'orgs.sede_plan_hint',
                    'Obligatorio. Solo este plan se mostrará en registro y alta de usuarios de la sede. El ciclo se asigna solo (no se pide al usuario).',
                  )}
                </p>
                {!sedeCatalogPlans.length ? (
                  <p className="text-xs text-amber-700 mt-1">
                    {t('orgs.sede_plan_empty', 'No hay planes con check Sedes. Créalos en Maestros → Planes D28D antes de abrir la sede.')}
                  </p>
                ) : null}
              </div>

              <fieldset className="border border-slate-200 rounded-xl p-4 space-y-3">
                <legend className="text-sm font-semibold text-stone-800 px-1">
                  {t('orgs.sede_calendar_programs', 'Programas visibles en calendario')}
                </legend>
                {gymPrograms.length ? (
                  <ul className="space-y-2 max-h-40 overflow-y-auto">
                    {gymPrograms.map((prog) => (
                      <li key={prog.id}>
                        <label className="flex items-center gap-2 text-sm text-stone-800 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={formData.sede_calendar_program_ids.some((id) => String(id) === String(prog.id))}
                            onChange={() => toggleSedeProgram(prog.id)}
                          />
                          <span>{prog.name || prog.id}</span>
                        </label>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm text-amber-700">{t('orgs.sede_no_parent_programs', 'Sin programas del gym virtual padre.')}</p>
                )}
              </fieldset>

              <fieldset className="border border-violet-200 rounded-xl p-4 bg-violet-50/40 space-y-3">
                <legend className="text-sm font-semibold text-stone-800 px-1">
                  {t('orgs.sede_welcome_videos', 'Videos de bienvenida por programa')}
                </legend>
                <p className="text-xs text-stone-500">
                  {t(
                    'orgs.sede_welcome_videos_hint',
                    'Opcional. Override de esta sede. Si está vacío, el usuario ve el video del programa o el del ciclo.',
                  )}
                </p>
                {(formData.sede_calendar_program_ids || []).length === 0 ? (
                  <p className="text-sm text-amber-800">
                    {t(
                      'orgs.sede_welcome_videos_need_programs',
                      'Marca al menos un programa arriba para asignar su video de bienvenida.',
                    )}
                  </p>
                ) : (
                  gymPrograms
                    .filter((prog) => formData.sede_calendar_program_ids.some((id) => String(id) === String(prog.id)))
                    .map((prog) => (
                      <label key={prog.id} className="block text-sm">
                        <span className="font-semibold text-stone-800">{prog.name || prog.id}</span>
                        <input
                          type="url"
                          className="input mt-1 w-full"
                          placeholder="https://youtube.com/... o Zoom share"
                          value={(formData.sede_welcome_videos_by_program || {})[String(prog.id)] || ''}
                          onChange={(e) => {
                            const val = e.target.value;
                            setFormData((prev) => ({
                              ...prev,
                              sede_welcome_videos_by_program: {
                                ...(prev.sede_welcome_videos_by_program || {}),
                                [String(prog.id)]: val,
                              },
                            }));
                          }}
                        />
                      </label>
                    ))
                )}
              </fieldset>

              <GymMinimalBrandingFields
                gymId={formData.sede_gym_id ? Number(formData.sede_gym_id) : null}
                formData={{
                  brand_name: formData.sede_brand_name,
                  logo_url: formData.sede_logo_url,
                  primary_color: formData.sede_primary_color,
                  secondary_color: formData.sede_secondary_color,
                  support_whatsapp: formData.sede_support_whatsapp,
                  welcome_message: formData.sede_welcome_message,
                }}
                setFormData={(updater) => {
                  setFormData((prev) => {
                    const current = {
                      brand_name: prev.sede_brand_name,
                      logo_url: prev.sede_logo_url,
                      primary_color: prev.sede_primary_color,
                      secondary_color: prev.sede_secondary_color,
                      support_whatsapp: prev.sede_support_whatsapp,
                      welcome_message: prev.sede_welcome_message,
                    };
                    const next = typeof updater === 'function' ? updater(current) : updater;
                    return {
                      ...prev,
                      sede_brand_name: next.brand_name ?? prev.sede_brand_name,
                      sede_logo_url: next.logo_url ?? prev.sede_logo_url,
                      sede_primary_color: next.primary_color ?? prev.sede_primary_color,
                      sede_secondary_color: next.secondary_color ?? prev.sede_secondary_color,
                      sede_support_whatsapp: next.support_whatsapp ?? prev.sede_support_whatsapp,
                      sede_welcome_message: next.welcome_message ?? prev.sede_welcome_message,
                    };
                  });
                }}
                showBrandName
              />

              <fieldset className="border border-slate-200 rounded-xl p-4 space-y-3">
                <legend className="text-sm font-semibold text-stone-800 px-1">{t('users.sede_admin_section', 'Administrador de sede')}</legend>
                <div className="text-sm space-y-2">
                  <span className="font-semibold text-stone-800">{t('common.role', 'Rol')}</span>
                  <p className="text-stone-700">
                    {t('users.sede_role_current', 'Actual')}:{' '}
                    <strong>{formatRole(userRoles[0] || user.rol || '—')}</strong>
                  </p>
                  {!userRoles.includes('admin_sede') ? (
                    <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                      {t(
                        'users.sede_role_fix_hint',
                        'Esta sede debe tener rol Admin sede (no Gym virtual). Al guardar se asignará automáticamente admin_sede.',
                      )}
                    </p>
                  ) : (
                    <p className="text-xs text-stone-500">
                      {t('users.sede_role_ok', 'Rol correcto para operador de sede.')}
                    </p>
                  )}
                  <p className="text-xs text-stone-500">
                    {t(
                      'users.sede_role_readonly_note',
                      'En «Editar sede» el rol siempre queda como Admin sede al guardar. Para asignar otro rol, usa la edición de usuario.',
                    )}
                  </p>
                  {isSuperAdmin && onSwitchToUserEdit ? (
                    <button
                      type="button"
                      className="text-sm text-lime-700 underline hover:text-lime-900"
                      onClick={onSwitchToUserEdit}
                    >
                      {t('users.sede_switch_user_edit', 'Cambiar rol del administrador →')}
                    </button>
                  ) : null}
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <label className="block text-sm">
                    <span className="font-semibold text-stone-800">{t('common.name', 'Nombre')}</span>
                    <input name="nombre" value={formData.nombre} onChange={handleChange} required className="input mt-1 w-full" />
                  </label>
                  <label className="block text-sm">
                    <span className="font-semibold text-stone-800">{t('tenants.field_admin_email', 'Email admin (login)')}</span>
                    <input name="email" type="email" value={formData.email} onChange={handleChange} required className="input mt-1 w-full" />
                  </label>
                  <label className="block text-sm sm:col-span-2">
                    <span className="font-semibold text-stone-800">{t('tenants.field_admin_password', 'Nueva contraseña admin (opcional)')}</span>
                    <input name="sede_admin_password" type="password" value={formData.sede_admin_password} onChange={handleChange} className="input mt-1 w-full" autoComplete="new-password" />
                  </label>
                </div>
              </fieldset>
            </>
          ) : (
            <>
              <div className="grid sm:grid-cols-2 gap-4">
                <label className="block text-sm">
                  <span className="font-semibold text-stone-800">{t('common.name', 'Nombre')}</span>
                  <input name="nombre" value={formData.nombre} onChange={handleChange} required className="input mt-1 w-full" />
                </label>
                <label className="block text-sm">
                  <span className="font-semibold text-stone-800">{t('common.email', 'Email')}</span>
                  <input name="email" type="email" value={formData.email} onChange={handleChange} required className="input mt-1 w-full" />
                </label>
              </div>

              <PhoneField
                id="admin-edit-telefono"
                label={t('common.phone', 'Teléfono / WhatsApp')}
                value={formData.telefono || ''}
                onChange={(next) => setFormData((prev) => ({ ...prev, telefono: next }))}
                hint={t('users.phone_hint', 'Incluye el indicativo del país para habilitar WhatsApp (ej. Colombia +57).')}
              />

              {showMemberFields ? (
                <fieldset className="rounded-xl border border-stone-200 bg-stone-50/80 p-4 space-y-3">
                  <legend className="text-sm font-semibold text-stone-800 px-1">
                    {t('users.health_profile', 'Perfil de salud (registro)')}
                  </legend>
                  {(formData.tiene_restricciones || formData.soy_mama_posparto) ? (
                    <div className="flex flex-wrap gap-2">
                      {formData.tiene_restricciones ? (
                        <span className="badge badge-amber text-xs">
                          {t('users.dietary_restrictions_badge', 'Restricciones alimentarias')}
                        </span>
                      ) : null}
                      {formData.soy_mama_posparto ? (
                        <span className="badge badge-purple text-xs">
                          {t('users.postpartum_badge', 'Mamá posparto')}
                        </span>
                      ) : null}
                    </div>
                  ) : (
                    <p className="text-xs text-stone-500 m-0">
                      {t('users.health_profile_empty', 'Sin marcas de restricciones ni posparto en el alta.')}
                    </p>
                  )}
                  <label className="flex items-start gap-2 text-sm text-stone-700 cursor-pointer">
                    <input
                      type="checkbox"
                      name="tiene_restricciones"
                      checked={Boolean(formData.tiene_restricciones)}
                      onChange={handleChange}
                      className="mt-1"
                    />
                    <span>{t('users.dietary_restrictions_label', 'Tiene restricciones o recomendaciones alimentarias')}</span>
                  </label>
                  {formData.tiene_restricciones ? (
                    <label className="block text-sm">
                      <span className="font-semibold text-stone-800">
                        {t('users.dietary_restrictions_detail', 'Detalle de restricciones')}
                      </span>
                      <textarea
                        name="restricciones_detalles"
                        value={formData.restricciones_detalles || ''}
                        onChange={handleChange}
                        rows={3}
                        className="input mt-1 w-full resize-y"
                        placeholder={t('users.dietary_restrictions_placeholder', 'Alergias, intolerancias, condiciones médicas…')}
                      />
                    </label>
                  ) : null}
                  <label className="flex items-start gap-2 text-sm text-stone-700 cursor-pointer">
                    <input
                      type="checkbox"
                      name="soy_mama_posparto"
                      checked={Boolean(formData.soy_mama_posparto)}
                      onChange={handleChange}
                      className="mt-1"
                    />
                    <span>{t('users.postpartum_label', 'Soy mamá posparto')}</span>
                  </label>
                </fieldset>
              ) : null}

              {isCoachActor && !isSuperAdmin && (
                <div className="rounded-xl border border-lime-200 bg-lime-50/80 p-4 text-sm text-stone-700 space-y-3">
                  <p className="font-semibold text-stone-900">{t('users.coach_client_hint_title', 'Cliente de entrenamiento')}</p>
                  <p>
                    {t(
                      'users.coach_client_hint_body',
                      'Puedes editar nombre, correo y el plan de activación. El Plan de alimentación no se asigna aquí: el cliente debe comprarlo en Mi cuenta (pasarela Food Plan).',
                    )}
                  </p>
                  {user.subscription?.has_account ? (
                    <p className="text-stone-600">
                      {t('users.sub_status', 'Estado')}:{' '}
                      <span className={`badge ${SUBSCRIPTION_ESTADO_BADGE[String(user.subscription.estado || '').toLowerCase()] || 'badge-slate'} text-xs`}>
                        {formatSubscriptionEstado(user.subscription, t)}
                      </span>
                    </p>
                  ) : null}
                  <label className="block text-sm">
                    <span className="font-semibold text-stone-800">{t('plans.subscription', 'Plan de activación')}</span>
                    {coachTrainingPlans.length === 0 ? (
                      <p className="text-xs text-amber-800 mt-1">
                        {t('plans.coach_activation_empty', 'No hay planes Training (audiencia coach) disponibles.')}
                      </p>
                    ) : (
                      <select
                        name="planId"
                        value={formData.planId || ''}
                        onChange={handleChange}
                        className="input mt-1 w-full"
                      >
                        <option value="">{t('common.select', 'Seleccionar…')}</option>
                        {coachTrainingPlans.map((p) => (
                          <option key={p.nombre} value={p.nombre}>
                            {(p.display_nombre || p.nombre).toUpperCase()}
                            {p.cycles_count ? ` · ${p.cycles_count} mes${Number(p.cycles_count) === 1 ? '' : 'es'}` : ''}
                          </option>
                        ))}
                      </select>
                    )}
                  </label>
                  {user.module_access?.food_plan ? (
                    <p className="text-emerald-800 text-xs">
                      {t('users.coach_food_active_hint', 'Plan de alimentación activo (comprado por el cliente).')}
                    </p>
                  ) : (
                    <p className="text-amber-800 text-xs">
                      {t('users.coach_food_pending_hint', 'Sin plan de alimentación — el cliente puede contratarlo en Mi cuenta.')}
                    </p>
                  )}
                </div>
              )}

              {isGymSimpleForm && isPrivilegedTarget && (
                <div className="rounded-xl border border-slate-200 bg-stone-50 p-4 text-sm space-y-2">
                  <p className="font-semibold text-stone-800">{t('common.roles', 'Rol')}</p>
                  <p className="text-stone-700">{userRoles.map(formatRole).join(', ')}</p>
                  <p className="text-xs text-stone-500">
                    {t('users.gym_role_readonly_hint', 'Los permisos los define el rol asignado; no se editan desde aquí.')}
                  </p>
                </div>
              )}

              {!isGymSimpleForm && (isSuperAdmin || (isPlatformD28dAdmin && isD28dDomainUser(user))) && (
                <fieldset className="border border-slate-200 rounded-xl p-4 space-y-2">
                  <legend className="text-sm font-semibold text-stone-800 px-1">{t('common.role', 'Rol')}</legend>
                  <p className="text-xs text-stone-500 mb-2">
                    {t('users.single_role_hint', 'Cada usuario tiene un solo rol. Elige uno.')}
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {roleOptionsForForm.map((role) => (
                      <label key={role.value} className="flex items-center gap-2 text-sm text-stone-700 bg-stone-50 p-2 rounded-lg border border-stone-200 cursor-pointer">
                        <input
                          type="radio"
                          name="edit_user_role"
                          checked={formData.roles[0] === role.value}
                          onChange={() => {
                            setFormData((prev) => ({
                              ...prev,
                              roles: [role.value],
                            }));
                          }}
                          className="border-stone-300 text-lime-600 focus:ring-lime-500"
                        />
                        {role.label}
                      </label>
                    ))}
                  </div>
                </fieldset>
              )}

              {showMemberFields && isGymScoped && (
                <div className="text-sm">
                  <span className="font-semibold text-stone-800">{t('common.gym', 'Gimnasio')}</span>
                  <p className="input mt-1 bg-stone-50 text-stone-700">
                    {(() => {
                      const gid = userGymIdFromUser(user);
                      return gid ? (gymById[gid]?.nombre || scopeGymName || '—') : (scopeGymName || '—');
                    })()}
                  </p>
                </div>
              )}

              {showTrainingSubscription && !isCoachActor && (
                <fieldset className="border border-lime-200 rounded-xl p-4 bg-lime-50/50 space-y-3">
                  <legend className="text-sm font-semibold text-stone-800 px-1">{t('users.subscription_section', 'Suscripción Training')}</legend>
                  <p className="text-sm text-stone-600">
                    Cliente del entrenador. La vigencia Training se aplica al pagar en «Activar clientes» (Wompi), no con el modal D28D. Puedes indicar días al guardar si un administrador confirma el pago.
                  </p>
                  {user.subscription?.has_account ? (
                    <div className="flex flex-wrap gap-3 text-sm text-stone-700">
                      <span>
                        {t('users.sub_status', 'Estado')}:{' '}
                        <span className={`badge ${SUBSCRIPTION_ESTADO_BADGE[String(user.subscription.estado || '').toLowerCase()] || 'badge-slate'} text-xs`}>
                          {formatSubscriptionEstado(user.subscription, t)}
                        </span>
                      </span>
                    </div>
                  ) : null}
                  <label className="block text-sm">
                    <span className="font-semibold text-stone-800">{t('users.extend_days_on_save', 'Días de vigencia al activar')}</span>
                    <input
                      name="extend_vigencia_days"
                      type="number"
                      min="1"
                      max="365"
                      value={formData.extend_vigencia_days}
                      onChange={handleChange}
                      className="input mt-1 w-full max-w-xs"
                      placeholder="30"
                    />
                  </label>
                  {/* Training: vigencia vía «Activar clientes» (Wompi). No modal D28D. */}
                </fieldset>
              )}

              {showPlanFields && (
                <>
                  {showD28dPlacement ? (
                    <div className="grid sm:grid-cols-2 gap-4">
                      <label className="block text-sm">
                        <span className="font-semibold text-stone-800">{t('users.program', 'Programa D28D')}</span>
                        <select name="d28d_program" value={currentProgramId} onChange={handleChange} className="input mt-1 w-full">
                          <option value="">{t('common.none', 'Ninguno')}</option>
                          {gymPrograms.map((p) => (
                            <option key={p.id} value={p.id}>{p.name || p.id}</option>
                          ))}
                        </select>
                      </label>
                      {foodPlanSelected && wantsD28dAddon ? (
                        <label className="block text-sm">
                          <span className="font-semibold text-stone-800">Plan D28D</span>
                          <select
                            name="d28d_addon_plan"
                            value={formData.d28d_addon_plan || addonPlan?.nombre || ''}
                            onChange={handleChange}
                            className="input mt-1 w-full"
                          >
                            {d28dAddonPlans.map((p) => (
                              <option key={p.nombre} value={p.nombre}>{p.nombre}</option>
                            ))}
                          </select>
                        </label>
                      ) : null}
                    </div>
                  ) : null}

                  {renderSubscriptionSection(
                    t('users.sede_member_pending', 'Usuario de sede sin cuenta activa. Asigna un plan y actívalo para habilitar el acceso.'),
                  )}
                </>
              )}

              {showMemberFields && !isGymSimpleForm && !isCoachActor && (
                <>
                  {!isCoachActor && !isGymScoped && showD28dPlacement && (
                    <label className="block text-sm">
                      <span className="font-semibold text-stone-800">{t('common.gym', 'Gimnasio')}</span>
                      <select name="gym_id" value={formData.gym_id} onChange={handleChange} className="input mt-1 w-full">
                        {!isPlatformD28dAdmin || foodPlanSelected || isTrainingClient ? (
                          <option value="">{t('common.none', 'Ninguno')}</option>
                        ) : null}
                        {gyms.map((g) => (
                          <option key={g.id} value={g.id}>{gymSelectOptionLabel(g, t)}</option>
                        ))}
                      </select>
                    </label>
                  )}
                  {isGymScoped && (
                    <div className="text-sm">
                      <span className="font-semibold text-stone-800">{t('common.gym', 'Gimnasio')}</span>
                      <p className="input mt-1 bg-stone-50 text-stone-700">{scopeGymName || gymById[Number(scopeGymId)]?.nombre || '—'}</p>
                    </div>
                  )}
                  {(isTrainingClient || (!isCoachActor && !isPlatformD28dAdmin && !isD28dDomainUser(user))) && (
                    <label className="block text-sm">
                      <span className="font-semibold text-stone-800">{t('common.trainer', 'Entrenador')}</span>
                      <select name="trainer_id" value={formData.trainer_id} onChange={handleChange} className="input mt-1 w-full">
                        <option value="">{t('common.none', 'Ninguno')}</option>
                        {trainers.map((tr) => (
                          <option key={tr.id} value={tr.id}>{tr.nombre}</option>
                        ))}
                      </select>
                    </label>
                  )}
                  {isSuperAdmin && (
                  <fieldset className="border border-slate-200 rounded-xl p-4 space-y-2">
                    <legend className="text-sm font-semibold text-stone-800 px-1">{t('users.modules', 'Módulos activos')}</legend>
                    {foodPlanSelected ? (
                      <p className="text-xs text-stone-500">
                        Food Plan se mantiene. Marca Programas D28D / Clases en vivo para crear el producto híbrido (cuenta D28D aparte).
                      </p>
                    ) : null}
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                      {MODULE_OPTIONS.map(({ key, label }) => {
                        const lockFood = foodPlanSelected && key === 'food_plan';
                        const checked = lockFood
                          ? true
                          : Boolean(formData.module_access?.[key]);
                        return (
                        <label key={key} className="flex items-center gap-2 text-sm text-stone-700 bg-stone-50 p-2 rounded-lg border border-stone-200 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={checked}
                            disabled={lockFood}
                            onChange={() => {
                              if (lockFood) return;
                              setFormData((prev) => {
                                const nextVal = !prev.module_access?.[key];
                                const ma = {
                                  ...prev.module_access,
                                  [key]: nextVal,
                                  food_plan: foodPlanSelected ? true : prev.module_access?.food_plan,
                                  nutrition: foodPlanSelected ? true : prev.module_access?.nutrition,
                                };
                                if (nextVal && (key === 'live_classes' || key === 'd28d')) {
                                  ma.d28d = true;
                                  ma.live_classes = true;
                                }
                                return { ...prev, module_access: ma };
                              });
                            }}
                            className="rounded border-stone-300 text-lime-600 focus:ring-lime-500"
                          />
                          {label}
                        </label>
                        );
                      })}
                    </div>
                  </fieldset>
                  )}
                </>
              )}
            </>
          )}

          <div className="flex gap-2 pt-2">
            <button type="button" className="btn-secondary flex-1" onClick={onClose} disabled={saving}>
              {t('common.cancel', 'Cancelar')}
            </button>
            <button type="submit" className="btn-primary flex-1" disabled={saving || (!isSedeEdit && isSuperAdmin && !isGymSimpleForm && formData.roles.length === 0 && !isConsumer)}>
              {saving ? t('common.saving', 'Guardando…') : t('common.save', 'Guardar')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  if (typeof document === 'undefined') return modal;
  return createPortal(modal, document.body);
}
